import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { format, addDays, startOfWeek, parseISO, isWithinInterval } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, BookUser, X as XIcon } from "lucide-react";
import RubricaPicker from "@/components/RubricaPicker";

const STATUS_COLORS = {
  confirmed: "bg-stone-700 text-stone-50",
  checked_in: "bg-blue-700 text-stone-50",
  checked_out: "bg-stone-300 text-slate-700",
  cancelled: "bg-red-100 text-red-700",
};

const dayCount = 14;

export default function CalendarPage() {
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [guests, setGuests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [start, setStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [showForm, setShowForm] = useState(false);
  const [showOccupants, setShowOccupants] = useState(false);
  const emptyForm = {
    guest_id: "", room_id: "", company_id: "",
    check_in: "", check_out: "",
    num_guests: 1, exempt_count: 0, total_price: 0,
    payment_method: "", payment_status: "non_pagato", paid_amount: 0,
    send_email: false, send_sms: false, occupants: [],
    privacy_notice_delivered: false,
    privacy_notice_delivery_method: "in_person",
  };
  const [form, setForm] = useState(emptyForm);
  const [pickerOpen, setPickerOpen] = useState(null); // null | "guest" | "company"
  const [isCompanyMode, setIsCompanyMode] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);

  const selectedGuest = useMemo(() => guests.find(g => g.id === form.guest_id) || null, [guests, form.guest_id]);
  const selectedCompany = useMemo(() => companies.find(c => c.id === form.company_id) || null, [companies, form.company_id]);

  const setOccupantField = (idx, field, value) => {
    setForm(f => {
      const next = [...(f.occupants || [])];
      const current = next[idx] || { _key: `occ-${Date.now()}-${idx}`, first_name: "", last_name: "", birth_date: "", exempt: false, exempt_reason: "" };
      next[idx] = { ...current, [field]: value };
      return { ...f, occupants: next };
    });
  };

  const ensureOccupants = (n) => {
    setForm(f => {
      const arr = [...(f.occupants || [])];
      while (arr.length < n) arr.push({ _key: `occ-${Date.now()}-${arr.length}-${Math.random().toString(36).slice(2,7)}`, first_name: "", last_name: "", birth_date: "", exempt: false, exempt_reason: "" });
      arr.length = n;
      return { ...f, occupants: arr };
    });
  };

  const days = useMemo(() => Array.from({ length: dayCount }, (_, i) => addDays(start, i)), [start]);

  const load = useCallback(async () => {
    try {
      const [r, b, g, c] = await Promise.all([
        api.get("/rooms"),
        api.get("/bookings"),
        api.get("/guests"),
        api.get("/companies").catch(() => ({ data: [] })),
      ]);
      setRooms(r.data); setBookings(b.data); setGuests(g.data); setCompanies(c.data);
    } catch (err) {
      // 402 → account sospeso, BillingLockGuard sta gestendo il redirect.
      if (err?.response?.status !== 402) {
        toast.error(err?.response?.data?.detail || "Errore");
      }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bookingForCell = (room, day) => bookings.find(b => {
    try {
      return b.room_id === room.id && b.status !== "cancelled" &&
        isWithinInterval(day, { start: parseISO(b.check_in), end: addDays(parseISO(b.check_out), -1) });
    } catch { return false; }
  });

  const checkIn = async (id) => { await api.post(`/bookings/${id}/check-in`); toast.success("Check-in effettuato"); load(); };
  const checkOut = async (id) => { await api.post(`/bookings/${id}/check-out`); toast.success("Check-out effettuato"); load(); };

  const onReview = (e) => {
    e.preventDefault();
    if (!form.guest_id) { toast.error("Scegli un ospite dalla rubrica"); return; }
    if (isCompanyMode && !form.company_id) { toast.error("Scegli un'azienda dalla rubrica"); return; }
    if (!form.room_id) { toast.error("Seleziona una camera"); return; }
    if (!form.check_in || !form.check_out) { toast.error("Imposta le date di check-in e check-out"); return; }
    setConfirmOpen(true);
  };

  const onConfirmSave = async () => {
    setSubmitting(true);
    try {
      const occupants = (form.occupants || []).filter(o => (o.first_name || o.last_name));
      const payload = {
        ...form,
        num_guests: Number(form.num_guests),
        exempt_count: Number(form.exempt_count || 0),
        total_price: Number(form.total_price),
        paid_amount: Number(form.paid_amount || 0),
        company_id: form.company_id || null,
        payment_method: form.payment_method || null,
        occupants,
        privacy_notice_delivered: !!form.privacy_notice_delivered,
        privacy_notice_delivery_method: form.privacy_notice_delivered ? form.privacy_notice_delivery_method : null,
      };
      const { data: created } = await api.post("/bookings", payload);
      if (form.privacy_notice_delivered && form.privacy_notice_delivery_method === "email") {
        try {
          await api.post(`/bookings/${created.id}/send-privacy-notice`);
          toast.success("Prenotazione creata · informativa privacy inviata via email");
        } catch (err) {
          toast.warning("Prenotazione creata, ma invio informativa fallito: " + (err?.response?.data?.detail || "errore"));
        }
      } else if (!form.privacy_notice_delivered) {
        toast.warning("Prenotazione creata — ricorda di consegnare l'informativa privacy all'ospite (GDPR art. 13)");
      } else {
        toast.success("Prenotazione creata");
      }
      setConfirmOpen(false);
      setShowForm(false);
      setShowOccupants(false);
      setForm(emptyForm);
      setIsCompanyMode(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-[1400px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracked text-blue-700 mb-2">CALENDARIO</p>
          <h1 className="font-heading font-semibold text-4xl tracking-tight">Prenotazioni</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setStart(addDays(start, -7))} className="border border-slate-300 p-2 hover:bg-slate-100" data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-slate-600">{format(start, "d MMM", { locale: it })} – {format(addDays(start, dayCount - 1), "d MMM yyyy", { locale: it })}</span>
          <button onClick={() => setStart(addDays(start, 7))} className="border border-slate-300 p-2 hover:bg-slate-100" data-testid="next-week"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setShowForm(true)} data-testid="new-booking"
            className="ml-4 inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-2 text-sm rounded-lg hover:bg-stone-800">
            <Plus className="w-4 h-4" /> Nuova prenotazione
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={onReview} className="border border-slate-200 bg-white p-6 mb-6 grid md:grid-cols-4 gap-3 text-sm" data-testid="booking-form">
          <div className="md:col-span-4 grid md:grid-cols-4 gap-3 pb-3 border-b border-slate-200">
            <div className="md:col-span-4 flex items-center gap-4 text-xs tracked text-slate-500">
              <span>INTESTATARIO</span>
              <label className="flex items-center gap-2 text-slate-700 normal-case tracking-normal">
                <input type="radio" name="intestatario" checked={!isCompanyMode} onChange={() => { setIsCompanyMode(false); setForm(f => ({ ...f, company_id: "" })); }} data-testid="booking-mode-private" /> Privato
              </label>
              <label className="flex items-center gap-2 text-slate-700 normal-case tracking-normal">
                <input type="radio" name="intestatario" checked={isCompanyMode} onChange={() => { setIsCompanyMode(true); setForm(f => ({ ...f, company_id: companies[0]?.id || "" })); }} data-testid="booking-mode-company" /> Azienda
              </label>
            </div>
            {isCompanyMode && (
              <div className="md:col-span-4 flex flex-wrap items-center gap-3 bg-blue-50/60 border border-blue-200 rounded-lg p-3" data-testid="booking-company">
                <button type="button" onClick={() => setPickerOpen("company")}
                  className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 py-2 text-xs rounded-lg hover:bg-blue-800"
                  data-testid="pick-company">
                  <BookUser className="w-4 h-4" /> Scegli dalla rubrica
                </button>
                {selectedCompany ? (
                  <>
                    <span className="text-sm text-slate-700">
                      <strong>{selectedCompany.ragione_sociale}</strong>
                      {selectedCompany.partita_iva ? <span className="text-slate-500"> · P. IVA {selectedCompany.partita_iva}</span> : null}
                    </span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, company_id: "" }))}
                      className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1" aria-label="Rimuovi azienda">
                      <XIcon className="w-3.5 h-3.5" /> Rimuovi
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-slate-500">
                    {companies.length === 0
                      ? "Nessuna azienda in rubrica — vai su «Aziende» per aggiungerne una."
                      : "Nessuna azienda selezionata — scegline una dalla rubrica."}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-2 flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="booking-guest-row">
            <button type="button" onClick={() => setPickerOpen("guest")}
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-3 py-2 text-xs rounded-lg hover:bg-stone-800"
              data-testid="pick-guest">
              <BookUser className="w-4 h-4" /> {isCompanyMode ? "Referente dalla rubrica" : "Ospite dalla rubrica"}
            </button>
            {selectedGuest ? (
              <>
                <span className="text-sm text-slate-700">
                  <strong>{selectedGuest.first_name} {selectedGuest.last_name}</strong>
                  {selectedGuest.document_number ? <span className="text-slate-500"> · Doc. {selectedGuest.document_number}</span> : null}
                </span>
                <button type="button" onClick={() => setForm(f => ({ ...f, guest_id: "" }))}
                  className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1" aria-label="Rimuovi ospite">
                  <XIcon className="w-3.5 h-3.5" /> Rimuovi
                </button>
              </>
            ) : (
              <span className="text-xs text-slate-500">Nessun ospite selezionato — scegline uno dalla rubrica.</span>
            )}
            {/* fallback nascosto per far scattare la validazione HTML required */}
            <input type="hidden" value={form.guest_id} required data-testid="booking-guest" readOnly />
          </div>
          <select required value={form.room_id} onChange={(e) => setForm(f => ({ ...f, room_id: e.target.value }))} className={inputCls} data-testid="booking-room">
            <option value="">Camera…</option>
            {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <input required type="date" value={form.check_in} onChange={(e) => setForm(f => ({ ...f, check_in: e.target.value }))} className={inputCls} data-testid="booking-checkin" />
          <input required type="date" value={form.check_out} onChange={(e) => setForm(f => ({ ...f, check_out: e.target.value }))} className={inputCls} data-testid="booking-checkout" />
          <input type="number" min={1} data-testid="booking-num-guests" value={form.num_guests} onChange={(e) => setForm(f => ({ ...f, num_guests: e.target.value }))} className={inputCls} placeholder="N. ospiti" />
          <input type="number" min={0} value={form.exempt_count} onChange={(e) => setForm(f => ({ ...f, exempt_count: e.target.value }))} className={inputCls} placeholder="Esenti tassa" title="Numero ospiti esenti dalla tassa di soggiorno (bambini, invalidi...)" />
          <input type="number" step="0.01" value={form.total_price} onChange={(e) => setForm(f => ({ ...f, total_price: e.target.value }))} className={inputCls} placeholder="Totale €" data-testid="booking-total-price" />
          <div />

          <div className="md:col-span-4 grid md:grid-cols-4 gap-3 pt-3 border-t border-slate-200">
            <div className="md:col-span-4 text-xs tracked text-slate-500">PAGAMENTO</div>
            <select value={form.payment_method} onChange={(e) => setForm(f => ({ ...f, payment_method: e.target.value }))} className={inputCls} data-testid="booking-payment-method">
              <option value="">Metodo…</option>
              <option value="contanti">Contanti</option>
              <option value="carta">Carta di credito</option>
              <option value="pos">POS / Bancomat</option>
              <option value="bonifico">Bonifico bancario</option>
              <option value="booking_com">Booking.com</option>
              <option value="airbnb">Airbnb</option>
              <option value="expedia">Expedia</option>
              <option value="voucher">Voucher</option>
              <option value="altro">Altro</option>
            </select>
            <select value={form.payment_status} onChange={(e) => setForm(f => ({ ...f, payment_status: e.target.value }))} className={inputCls} data-testid="booking-payment-status">
              <option value="non_pagato">Non pagato</option>
              <option value="pagato">Pagato</option>
              <option value="parziale">Parzialmente pagato</option>
              <option value="rimborsato">Rimborsato</option>
            </select>
            <input type="number" step="0.01" min={0} value={form.paid_amount} onChange={(e) => setForm(f => ({ ...f, paid_amount: e.target.value }))} className={inputCls} placeholder="Acconto €" data-testid="booking-paid-amount" />
            <div />
          </div>

          <label className="flex items-center gap-2 text-slate-600"><input type="checkbox" checked={form.send_email} onChange={(e) => setForm(f => ({ ...f, send_email: e.target.checked }))} /> Email conferma</label>
          <label className="flex items-center gap-2 text-slate-600"><input type="checkbox" checked={form.send_sms} onChange={(e) => setForm(f => ({ ...f, send_sms: e.target.checked }))} /> SMS conferma</label>
          <div className="md:col-span-4 border-t border-slate-200 pt-3 mt-1">
            <button type="button" data-testid="toggle-occupants"
              onClick={() => { const n = Number(form.num_guests) || 1; ensureOccupants(n); setShowOccupants(s => !s); }}
              className="text-xs underline text-slate-600 hover:text-slate-900">
              {showOccupants ? "Nascondi" : "Aggiungi"} dettaglio ospiti (1 schedina Alloggiati per ogni ospite — obbligatorio se prenotazione aziendale)
            </button>
          </div>
          {showOccupants && (
            <div className="md:col-span-4 grid gap-2" data-testid="occupants-list">
              {(form.occupants || []).map((o, idx) => (
                <div key={o._key || `occ-${idx}`} className="grid md:grid-cols-6 gap-2 items-center bg-slate-50 p-2 border border-slate-200">
                  <span className="text-xs text-slate-500">Ospite {idx + 1}</span>
                  <input placeholder="Nome" value={o.first_name} onChange={(e) => setOccupantField(idx, "first_name", e.target.value)} className={inputCls} data-testid={`occupant-${idx}-first`} />
                  <input placeholder="Cognome" value={o.last_name} onChange={(e) => setOccupantField(idx, "last_name", e.target.value)} className={inputCls} data-testid={`occupant-${idx}-last`} />
                  <input type="date" placeholder="Nascita" value={o.birth_date || ""} onChange={(e) => setOccupantField(idx, "birth_date", e.target.value)} className={inputCls} data-testid={`occupant-${idx}-birth`} />
                  <label className="flex items-center gap-2 text-slate-600 text-xs"><input type="checkbox" checked={!!o.exempt} onChange={(e) => setOccupantField(idx, "exempt", e.target.checked)} data-testid={`occupant-${idx}-exempt`} /> Esente</label>
                  <select value={o.exempt_reason || ""} onChange={(e) => setOccupantField(idx, "exempt_reason", e.target.value)} className={inputCls} disabled={!o.exempt} data-testid={`occupant-${idx}-reason`}>
                    <option value="">Motivo…</option>
                    <option value="minore">Minore</option>
                    <option value="disabile">Disabile</option>
                    <option value="lunga_permanenza">Lunga permanenza</option>
                    <option value="autista">Autista</option>
                    <option value="forze_ordine">Forze ordine</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
              ))}
            </div>
          )}
          <div className="md:col-span-4 border-t-2 border-blue-200 bg-blue-50/60 -mx-6 px-6 pt-4 pb-4 mt-2" data-testid="booking-privacy-block">
            <div className="flex items-start gap-2 mb-2">
              <input
                id="bk-privacy-delivered"
                type="checkbox"
                checked={!!form.privacy_notice_delivered}
                onChange={(e) => setForm(f => ({ ...f, privacy_notice_delivered: e.target.checked }))}
                className="mt-1"
                data-testid="booking-privacy-attest"
              />
              <label htmlFor="bk-privacy-delivered" className="text-sm text-stone-800 leading-snug">
                <span className="font-medium">Confermo di aver consegnato l'informativa privacy all'ospite</span> ai sensi del Reg. UE 2016/679 (GDPR) art. 13 e del TULPS art. 109.{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-700 underline underline-offset-2" data-testid="booking-privacy-link">Apri informativa</a>
              </label>
            </div>
            {form.privacy_notice_delivered && (
              <div className="ml-6 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-700" data-testid="booking-privacy-method">
                <span className="tracked text-slate-500">MODALITÀ:</span>
                <label className="flex items-center gap-1.5"><input type="radio" name="pn-method" value="in_person" checked={form.privacy_notice_delivery_method === "in_person"} onChange={(e) => setForm(f => ({ ...f, privacy_notice_delivery_method: e.target.value }))} data-testid="booking-privacy-method-inperson" /> A voce / cartacea</label>
                <label className="flex items-center gap-1.5"><input type="radio" name="pn-method" value="email" checked={form.privacy_notice_delivery_method === "email"} onChange={(e) => setForm(f => ({ ...f, privacy_notice_delivery_method: e.target.value }))} data-testid="booking-privacy-method-email" /> Inviata via email (Xenia la invia automaticamente)</label>
                <label className="flex items-center gap-1.5"><input type="radio" name="pn-method" value="self_checkin" checked={form.privacy_notice_delivery_method === "self_checkin"} onChange={(e) => setForm(f => ({ ...f, privacy_notice_delivery_method: e.target.value }))} data-testid="booking-privacy-method-selfcheckin" /> Sarà raccolta al self check-in</label>
              </div>
            )}
            {!form.privacy_notice_delivered && (
              <p className="ml-6 text-xs text-blue-700">⚠ Se non spunti, la prenotazione viene salvata comunque ma non risulterà conforme. Potrai attestare in seguito dal dettaglio prenotazione.</p>
            )}
          </div>

          <div className="md:col-span-4 flex gap-2">
            <button data-testid="booking-submit" className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm rounded-lg hover:bg-stone-800">Rivedi e conferma</button>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-600 px-4 py-2 text-sm">Annulla</button>
          </div>
        </form>
      )}

      <RubricaPicker
        open={pickerOpen === "guest"}
        onClose={() => setPickerOpen(null)}
        kind="guest"
        items={guests}
        onPick={(g) => setForm(f => ({ ...f, guest_id: g.id }))}
      />
      <RubricaPicker
        open={pickerOpen === "company"}
        onClose={() => setPickerOpen(null)}
        kind="company"
        items={companies}
        onPick={(c) => setForm(f => ({ ...f, company_id: c.id }))}
      />

      <BookingConfirmDialog
        open={confirmOpen}
        submitting={submitting}
        form={form}
        isCompanyMode={isCompanyMode}
        guest={selectedGuest}
        company={selectedCompany}
        room={rooms.find(r => r.id === form.room_id) || null}
        onEdit={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
      />

      <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="calendar-grid">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="p-3 text-left border-b border-slate-200 sticky left-0 bg-white min-w-[140px]">Camera</th>
              {days.map(d => (
                <th key={d.toISOString()} className="p-2 border-b border-slate-200 text-center min-w-[70px]">
                  <div className="text-slate-500 uppercase tracking-wider">{format(d, "EEE", { locale: it })}</div>
                  <div className="font-medium">{format(d, "d")}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rooms.length === 0 && (
              <tr><td colSpan={dayCount + 1} className="p-12 text-center text-slate-500">Nessuna camera. Aggiungile prima.</td></tr>
            )}
            {rooms.map(room => (
              <tr key={room.id}>
                <td className="p-3 border-b border-slate-200 sticky left-0 bg-white font-medium">{room.name}<div className="text-slate-500 text-xs">{room.type}</div></td>
                {days.map(d => {
                  const b = bookingForCell(room, d);
                  return (
                    <td key={d.toISOString()} className="p-1 border-b border-slate-200 align-top">
                      {b && (
                        <div className={`relative px-2 py-1 text-[10px] rounded-lg cursor-pointer ${STATUS_COLORS[b.status] || "bg-stone-200"}`}
                          onClick={() => setDetailBooking(b)}
                          title="Tocca per aprire la scheda prenotazione"
                          data-testid={`booking-${b.id}`}>
                          {b.status}
                          {!b.privacy_notice_delivered && (
                            <span
                              className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"
                              title="Informativa privacy non ancora consegnata"
                              data-testid={`privacy-missing-${b.id}`}
                            />
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-stone-700 inline-block" /> Confermata</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-blue-700 inline-block" /> Check-in</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-stone-300 inline-block" /> Check-out</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Informativa privacy mancante</span>
        <span className="text-slate-500">· Tocca una prenotazione per aprire la scheda</span>
      </div>

      <BookingDetailDialog
        booking={detailBooking}
        guests={guests}
        rooms={rooms}
        onClose={() => setDetailBooking(null)}
        onChanged={() => { setDetailBooking(null); load(); }}
      />
    </div>
  );
}

const inputCls = "border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white";

function BookingConfirmDialog({ open, submitting, form, isCompanyMode, guest, company, room, onEdit, onConfirm }) {
  if (!open) return null;

  const nights = (() => {
    try {
      const ci = parseISO(form.check_in);
      const co = parseISO(form.check_out);
      const ms = co - ci;
      return Math.max(1, Math.round(ms / 86400000));
    } catch { return 1; }
  })();

  const total = Number(form.total_price || 0);
  const paid = Number(form.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const occupants = (form.occupants || []).filter(o => (o.first_name || o.last_name));

  const paymentLabel = {
    cash: "Contanti", card: "Carta", bank_transfer: "Bonifico", pos: "POS", other: "Altro",
  }[form.payment_method] || "Non specificato";

  const paymentStatusLabel = {
    unpaid: "Non pagato", deposit: "Acconto", paid: "Pagato interamente",
  }[form.payment_status] || form.payment_status;

  const privacyLabel = form.privacy_notice_delivered ? ({
    in_person: "Consegnata a voce / cartacea",
    email: "Inviata via email (automatica)",
    self_checkin: "Raccolta al self check-in",
  }[form.privacy_notice_delivery_method] || "Consegnata") : "⚠ Non ancora consegnata";

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onEdit}
      data-testid="booking-confirm-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-confirm-dialog"
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading font-semibold text-xl">Rivedi la prenotazione prima di salvare</h2>
          <p className="text-xs text-slate-500 mt-0.5">Controlla ogni dato. Se qualcosa non torna, torna indietro per correggere.</p>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4 text-sm">
          {isCompanyMode && company && (
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="confirm-section-company">
              <div className="text-xs tracked text-blue-700 mb-2">PRENOTAZIONE AZIENDALE</div>
              <div className="font-medium text-slate-900">{company.ragione_sociale}</div>
              {company.partita_iva && <div className="text-xs text-slate-600">P. IVA {company.partita_iva}</div>}
              {company.email && <div className="text-xs text-slate-600">{company.email}</div>}
            </section>
          )}

          <section className="grid grid-cols-2 gap-4" data-testid="confirm-section-stay">
            <ConfirmRow label={isCompanyMode ? "Referente" : "Ospite"} value={guest ? `${guest.first_name} ${guest.last_name}` : "—"} />
            <ConfirmRow label="Camera" value={room ? `${room.name} · ${room.type}` : "—"} />
            <ConfirmRow label="Check-in" value={form.check_in || "—"} />
            <ConfirmRow label="Check-out" value={form.check_out || "—"} />
            <ConfirmRow label="Notti" value={String(nights)} />
            <ConfirmRow label="Numero ospiti" value={String(form.num_guests || 1)} />
          </section>

          {occupants.length > 0 && (
            <section data-testid="confirm-section-occupants">
              <div className="text-xs tracked text-slate-500 mb-2">OCCUPANTI AGGIUNTIVI ({occupants.length})</div>
              <ul className="space-y-1 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {occupants.map((o, i) => (
                  <li key={i} className="text-sm text-slate-700">
                    {o.first_name} {o.last_name}
                    {o.document_number ? <span className="text-slate-500 text-xs"> · Doc. {o.document_number}</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="border-t border-slate-200 pt-4" data-testid="confirm-section-payment">
            <div className="text-xs tracked text-slate-500 mb-2">PAGAMENTO</div>
            <div className="grid grid-cols-2 gap-4">
              <ConfirmRow label="Totale" value={`€ ${total.toFixed(2)}`} strong />
              <ConfirmRow label="Acconto versato" value={`€ ${paid.toFixed(2)}`} />
              <ConfirmRow label="Saldo residuo" value={`€ ${balance.toFixed(2)}`} highlight={balance > 0} />
              <ConfirmRow label="Metodo" value={paymentLabel} />
              <ConfirmRow label="Stato" value={paymentStatusLabel} />
              {Number(form.exempt_count || 0) > 0 && (
                <ConfirmRow label="Esenti tassa soggiorno" value={String(form.exempt_count)} />
              )}
            </div>
          </section>

          <section className="border-t border-slate-200 pt-4" data-testid="confirm-section-privacy">
            <div className="text-xs tracked text-slate-500 mb-2">INFORMATIVA PRIVACY (GDPR)</div>
            <div className={form.privacy_notice_delivered ? "text-slate-700" : "text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"}>
              {privacyLabel}
            </div>
          </section>

          <section className="border-t border-slate-200 pt-4 text-xs text-slate-500" data-testid="confirm-section-notify">
            Notifiche automatiche all'ospite: {form.send_email_confirmation ? "email ✓" : "email —"} · {form.send_sms_confirmation ? "SMS ✓" : "SMS —"}
          </section>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex gap-2 justify-end">
          <button
            onClick={onEdit}
            disabled={submitting}
            data-testid="booking-confirm-edit"
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            ← Torna e modifica
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            data-testid="booking-confirm-save"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-blue-800 disabled:opacity-60"
          >
            {submitting ? "Salvataggio…" : "Conferma e crea prenotazione"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmRow({ label, value, strong, highlight }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-0.5 ${strong ? "font-semibold text-base" : ""} ${highlight ? "text-amber-700" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function BookingDetailDialog({ booking, guests, rooms, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  // Re-sync local form state whenever a new booking is opened
  useEffect(() => {
    if (booking) {
      setForm({
        check_in: booking.check_in || "",
        check_out: booking.check_out || "",
        room_id: booking.room_id || "",
        num_guests: booking.num_guests || 1,
        total_price: booking.total_price || 0,
        paid_amount: booking.paid_amount || 0,
        payment_method: booking.payment_method || "",
        payment_status: booking.payment_status || "",
        notes: booking.notes || "",
      });
      setEditing(false);
    }
  }, [booking]);

  if (!booking || !form) return null;

  const guest = guests.find(g => g.id === booking.guest_id);
  const room = rooms.find(r => r.id === booking.room_id);

  const doCheckIn = async () => { setBusy(true); try { await api.post(`/bookings/${booking.id}/check-in`); toast.success("Check-in effettuato"); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); } };
  const doCheckOut = async () => { setBusy(true); try { await api.post(`/bookings/${booking.id}/check-out`); toast.success("Check-out effettuato"); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); } };
  const doDelete = async () => {
    if (!window.confirm("Eliminare questa prenotazione? L'azione non è reversibile.")) return;
    setBusy(true);
    try { await api.delete(`/bookings/${booking.id}`); toast.success("Prenotazione eliminata"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const doSave = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        num_guests: Number(form.num_guests),
        total_price: Number(form.total_price),
        paid_amount: Number(form.paid_amount),
      };
      await api.patch(`/bookings/${booking.id}`, payload);
      toast.success("Prenotazione aggiornata");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const doAttestPrivacy = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/attest-privacy-notice`, { method: "in_person" });
      toast.success("Informativa privacy attestata");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="booking-detail-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-detail-dialog"
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-semibold text-xl">
              {guest ? `${guest.first_name} ${guest.last_name}` : "Prenotazione"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {room?.name || "—"} · {booking.check_in} → {booking.check_out} · <span className="font-medium">{booking.status}</span>
            </p>
          </div>
          <button onClick={onClose} aria-label="Chiudi"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 text-sm">
          {!booking.privacy_notice_delivered && (
            <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-lg p-3 flex items-start justify-between gap-3" data-testid="detail-privacy-warn">
              <div>
                <div className="font-medium">⚠ Informativa privacy non consegnata</div>
                <div className="text-xs mt-0.5">Attesta la consegna per rendere la prenotazione conforme GDPR.</div>
              </div>
              <button onClick={doAttestPrivacy} disabled={busy}
                className="shrink-0 text-xs bg-amber-700 text-white px-3 py-1.5 rounded-lg hover:bg-amber-800 disabled:opacity-50"
                data-testid="detail-attest-privacy">
                Attesta ora
              </button>
            </div>
          )}

          {editing ? (
            <div className="grid grid-cols-2 gap-3" data-testid="detail-edit-form">
              <Field label="Check-in"><input type="date" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} className={inputCls + " w-full"} data-testid="edit-checkin" /></Field>
              <Field label="Check-out"><input type="date" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} className={inputCls + " w-full"} data-testid="edit-checkout" /></Field>
              <Field label="Camera">
                <select value={form.room_id} onChange={e => setForm({ ...form, room_id: e.target.value })} className={inputCls + " w-full"} data-testid="edit-room">
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.name} · {r.type}</option>)}
                </select>
              </Field>
              <Field label="N. ospiti"><input type="number" min={1} value={form.num_guests} onChange={e => setForm({ ...form, num_guests: e.target.value })} className={inputCls + " w-full"} data-testid="edit-num-guests" /></Field>
              <Field label="Totale €"><input type="number" step="0.01" value={form.total_price} onChange={e => setForm({ ...form, total_price: e.target.value })} className={inputCls + " w-full"} data-testid="edit-total" /></Field>
              <Field label="Acconto €"><input type="number" step="0.01" value={form.paid_amount} onChange={e => setForm({ ...form, paid_amount: e.target.value })} className={inputCls + " w-full"} data-testid="edit-paid" /></Field>
              <Field label="Metodo"><select value={form.payment_method || ""} onChange={e => setForm({ ...form, payment_method: e.target.value })} className={inputCls + " w-full"}>
                <option value="">—</option><option value="cash">Contanti</option><option value="card">Carta</option><option value="bank_transfer">Bonifico</option><option value="pos">POS</option><option value="other">Altro</option>
              </select></Field>
              <Field label="Stato"><select value={form.payment_status || ""} onChange={e => setForm({ ...form, payment_status: e.target.value })} className={inputCls + " w-full"}>
                <option value="unpaid">Non pagato</option><option value="deposit">Acconto</option><option value="paid">Pagato interamente</option>
              </select></Field>
              <div className="col-span-2">
                <Field label="Note">
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls + " w-full"} data-testid="edit-notes" />
                </Field>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <ConfirmRow label="Camera" value={room ? `${room.name} · ${room.type}` : "—"} />
              <ConfirmRow label="N. ospiti" value={String(booking.num_guests || 1)} />
              <ConfirmRow label="Totale" value={`€ ${Number(booking.total_price || 0).toFixed(2)}`} strong />
              <ConfirmRow label="Acconto" value={`€ ${Number(booking.paid_amount || 0).toFixed(2)}`} />
              <ConfirmRow label="Saldo residuo" value={`€ ${Math.max(0, Number(booking.total_price || 0) - Number(booking.paid_amount || 0)).toFixed(2)}`} highlight={Number(booking.total_price || 0) - Number(booking.paid_amount || 0) > 0} />
              <ConfirmRow label="Metodo" value={booking.payment_method || "—"} />
              {booking.notes && <div className="col-span-2"><ConfirmRow label="Note" value={booking.notes} /></div>}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            {booking.status === "confirmed" && !editing && (
              <button onClick={doCheckIn} disabled={busy} data-testid="detail-checkin"
                className="bg-blue-700 text-white px-4 py-2 text-sm rounded-lg hover:bg-blue-800 disabled:opacity-50">
                Check-in
              </button>
            )}
            {booking.status === "checked_in" && !editing && (
              <button onClick={doCheckOut} disabled={busy} data-testid="detail-checkout"
                className="bg-stone-700 text-white px-4 py-2 text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50">
                Check-out
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button onClick={() => setEditing(false)} disabled={busy}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Annulla</button>
                <button onClick={doSave} disabled={busy} data-testid="detail-save"
                  className="bg-stone-900 text-white px-4 py-2 text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50">
                  {busy ? "Salvataggio…" : "Salva"}
                </button>
              </>
            ) : (
              <>
                <button onClick={doDelete} disabled={busy} data-testid="detail-delete"
                  className="text-red-700 hover:text-red-900 border border-red-300 hover:border-red-500 px-4 py-2 text-sm rounded-lg disabled:opacity-50">
                  Elimina
                </button>
                <button onClick={() => setEditing(true)} disabled={busy} data-testid="detail-edit"
                  className="bg-stone-900 text-white px-4 py-2 text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50">
                  Modifica
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

